import {
  CLIENT_TYPE,
  Condition,
  COUNTRIES,
  CreditRating,
  CurrencyType,
  DateFormat,
  FeeCategory,
  FinanceType,
  FixedValueFee,
  FuelEfficiencyUnit,
  IncentivesProvider,
  InventoryType,
  Language,
  Origin,
  PriceOverrideSource,
  SpecialFees,
  TaxType,
} from '@upstart/harmony/lib/constants';
import {
  InStoreControlPanelSteps,
  OnlineControlPanelSteps,
} from '@upstart/harmony/lib/flow-utils';
import {
  OwnershipRule,
  PriceRules,
} from '@upstart/harmony/lib/types/dealerTypes';
import {
  CreditPricingLeasePricing,
  CreditPricingLeaseTerm,
  CreditPricingPurchasePricing,
  CreditPricingPurchaseTerm,
  CreditTier,
} from '@upstart/harmony/lib/legos/creditPricingType';

// this is an incomplete definition of the dealer object
// todo share with backend

export type TaxBreakdown = {
  [index in TaxType]?: {
    name: string;
    rate: number;
    useRate?: number; // defaults to rate, only for lease
    maxAmount?: number;
    // only filled in when describing the tax amount for a car in payments
    amount?: number;
  };
};

export interface ValueDealerFee {
  type: typeof FixedValueFee;
  name: string;
  value: number | undefined;
  category?: string;
  includeInFeeStack: boolean;
}

export interface AprDealerFee {
  type: SpecialFees;
  name: string;
  apr: number;
  category?: string;
}

export type DealerFee = ValueDealerFee | AprDealerFee;

export function isFixedValueDealerFee(fee: DealerFee): fee is ValueDealerFee {
  return fee.type === FixedValueFee;
}

export type DealerFees = {
  [index in Condition]?: {
    [index2 in FinanceType]?: {
      [index3 in FeeCategory]?: DealerFee[];
    };
  };
};

type CreditTiers = {
  [index in CreditRating]: {
    low?: number;
    high?: number;
  };
};

interface PurchaseTerms {
  term1: number;
  term2: number;
  term3: number;
}

interface PurchaseInfo {
  terms?: PurchaseTerms;
  credit: {
    [index in CreditRating]: {
      apr?: PurchaseTerms;
    };
  };
}

export interface LeaseMileages {
  mileage1: number;
  mileage2: number;
  mileage3: number;
  mileage4?: number;
  mileage5?: number;
}

export interface LeaseTerm {
  term?: number;
  credit: {
    [index in CreditRating]: {
      moneyFactor?: LeaseMileages;
    };
  };
}

export interface LeaseTerms {
  term1: LeaseTerm;
  term2?: LeaseTerm;
  term3?: LeaseTerm;
  term4?: LeaseTerm;
  term5?: LeaseTerm;
}

export interface LeaseInfo {
  terms?: LeaseTerms;
  mileages?: LeaseMileages;
  credit: {
    [index in CreditRating]: {
      moneyFactor?: LeaseMileages;
    };
  };
}

export interface AprConfig {
  enabled?: boolean;
  creditTiers?: CreditTiers;
  purchase?: PurchaseInfo;
  lease?: LeaseInfo;
  options: {
    useManufacturerApr?: {
      purchase?: boolean;
      [CreditRating.EXCELLENT]?: boolean;
      [CreditRating.GOOD]?: boolean;
      [CreditRating.AVERAGE]?: boolean;
      [CreditRating.POOR]?: boolean;
    };
    useManufacturerRebate?: {
      purchase?: boolean;
      lease?: boolean;
      cash?: boolean;
    };
  };
}

interface AprConfigWithCondition {
  [Condition.NEW]?: AprConfig;
  [Condition.USED]?: AprConfig;
}

export interface Day {
  enabled: boolean;
  start: number;
  end: number;
}

export interface Days {
  sunday: Day;
  monday: Day;
  tuesday: Day;
  wednesday: Day;
  thursday: Day;
  friday: Day;
  saturday: Day;
}

interface FirstlookCredentials {
  dealerId: string;
  passcode: string;
  partnerId: string | number;
}

interface VautoCredentials {
  username: string;
  password: string;
}

interface Manager {
  name?: string;
  email?: string;
  phone?: string;
  shouldEmailLeaderboard?: boolean;
  shouldSendTurnover?: boolean;
  shouldSendLead?: boolean;
}

export interface LaunchPadFeatures {
  online?: boolean;
  instore?: boolean;
}

export interface Dealer {
  id: string;
  name?: string | null;
  logo?: string | null;
  brand?: string[] | null;
  address?: string | null;
  phone?: string | null;
  isIndependent?: boolean;
  dealerWebsiteUrl?: string | null;
  privacyPolicyUrl?: string | null;
  dealerType?: string | null;
  city?: string | null;
  county?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: COUNTRIES | null;
  timezone?: string | null;

  testDriveSpeed?: number | null;
  testDriveDuration?: number | null;

  taxes?: { [index in FinanceType]?: TaxBreakdown } | null;
  printingType?: string | null; // PrintingType
  managers?: Manager[] | null;
  stepFlowConfig?: {
    [CLIENT_TYPE.IN_STORE]?: typeof InStoreControlPanelSteps[keyof typeof InStoreControlPanelSteps][];
    [CLIENT_TYPE.ONLINE]?: typeof OnlineControlPanelSteps[keyof typeof OnlineControlPanelSteps][];
  };
  Feeds?: Record<string, unknown>[] | null;

  crmType?: string | null; // CrmType
  crmEmail?: string | null; // ADF Email, deprecated REL-6569
  crmEmailNew?: string | null; // ADF Email for new cars
  crmEmailUsed?: string | null; // ADF Email for used cars
  crmDefaults?: {
    type?: string;
    sourceId?: string;
  } | null;
  crmConfig?: {
    pushToDms: boolean;
  };
  dealersocket?: {
    dealersocketId: string;
    dealersocketSiteNumber: string;
  };
  reynolds?: {
    defaultStatusId: string;
    demoCounterText?: string;
  };
  vinsolutions?: {
    vinsolutionsId: string;
  };
  fniType?: string | null; // FNIType
  dealertrack?: {
    dealertrackId: string;
  };
  routeone?: {
    routeOneId: string;
  };
  cudl?: {
    cudlId: string;
  };

  dmsType?: string | null; // dmsType
  financeEmail?: string;

  lending?: {
    businessInfo?: {
      dealershipName: string;
      dealershipGroup: string;
    };
    contactInfo?: {
      name: string;
      phone: string;
    };
    businessHours?: Days;
    paymentInfo?: {
      email: string;
      bankName: string;
      accountName: string;
      routingNumber: string;
      accountNumber: string;
    };
    submittedLendingInfo?: boolean;
  };

  tradeInManagerType?: string | null; // TradeInType
  firstlook?: FirstlookCredentials | null;
  vauto?: {
    enabled?: {
      [index in Origin.ONLINE | Origin.INSTORE]?: boolean;
    };
    entityId?: string;
    username?: string;
    login?: VautoCredentials;
  } | null;

  wizard?: {
    newWizard?: {
      enabled?: boolean;
    };
    pickup?: {
      enabled: boolean;
      appointmentWindow: string;
      startsAfter: string;
      endsAfter: string;
      days?: Days;
    };
    delivery?: {
      enabled: boolean;
      startsAfter: string;
      endsAfter: string;
      radius: number;
      days?: Days;
    };
    splashPage?: {
      alternateHeaders?: boolean;
    };
    paymentBuilder?: {
      showPaymentSummary?: boolean;
    };
    leadForm?: {
      piiFlow?: number;
    };
    updateEmails?: {
      enabled: boolean;
      recipientEmail?: string;
    };
  } | null;

  desking?: {
    addInceptionFeesToTotalAtStart?: boolean;
    enabled: boolean;
    defaultPencil: boolean;
    managerDesking?: boolean;
    salespersonDesking?: boolean;
    onlineDesking?: boolean;
    useATCTaxes?: boolean;
    useATCFees?: boolean;
    showAdditionalCustomerIncentives?: boolean;
    showAllAdditionalCustomerIncentives?: boolean;
    showSeparateLowInterestHighRebateOffers?: boolean;
    useTradeInAllowanceForTax?: {
      enabled: boolean;
      [Condition.NEW]: boolean;
      [Condition.USED]: boolean;
    };
    inceptionFeesTotalAtStart?: {
      [index: string]: any;
    };
    ignoreMRM?: boolean;
    applyRebatesToDueAtSigning?: boolean;
    enableOnePayLease?: boolean;
  };

  fees?: DealerFees | null;
  priceRules?: PriceRules | null;
  aprConfig?: AprConfigWithCondition | null;
  inStoreAprConfig?: AprConfigWithCondition | null;

  tabletFormsConfig?: {
    customerInfo?: {
      email: {
        required: boolean;
      };
      phone: {
        required: boolean;
      };
    };
    vehicleSelection?: {
      daysUntilAgedUnit?: {
        [index in Condition]: number | null;
      };
    };
    fiveLine?: {
      enabled: boolean;
    };
  };

  featureConfig?: {
    shiftDigital?: {
      enabled: boolean;
    };
    unite?: {
      enabled?: boolean;
    };
    thirdPartyLeads?: {
      forwardEmailToCustomer: boolean;
      forwardTextToCustomer: boolean;
    };
    globalPricing?: {
      usePriceOverrides?: { [index in PriceOverrideSource]?: boolean };
      forwardedPriceOverrideExpiration?: number; // in # of days
    };
    virtualShowroom?: {
      enabled: boolean;
    };
    incentives?: {
      provider?: IncentivesProvider;
    };
    inventoryImages?: {
      chromeDataIsPrimaryImageSource?: boolean;
    };
    downPayment?: {
      default?: number;
    };
    useCPO?: {
      enabled: boolean;
    };
  } | null;

  localizationConfig?: {
    defaultLanguage?: Language;
    supportedLanguages?: Language[];
    dateFormat?: DateFormat;
    fuelEfficiencyUnit?: FuelEfficiencyUnit;
    currency?: CurrencyType;
  } | null;

  ownershipOptionsRules?: {
    [Condition.NEW]: {
      [index in CLIENT_TYPE]: {
        [index2 in FinanceType]: OwnershipRule;
      };
    };
    [Condition.USED]: {
      [index in CLIENT_TYPE]: {
        [index2 in FinanceType.CASH | FinanceType.PURCHASE]: OwnershipRule;
      };
    };
    [Condition.CPO]?: {
      [index in CLIENT_TYPE]: {
        [index2 in FinanceType.CASH | FinanceType.PURCHASE]: OwnershipRule;
      };
    };
  };

  launchpad?: {
    pricingValidation?: {
      [index in PricingValidationType]?: {
        [index2 in PricingValidationClientType]?: FinanceTypePricingValidation;
      };
    };
    printSetup?: {
      isAirprintSupported?: boolean;
      isOnSameWifiNetwork?: boolean;
      hasFormLibrarySelected?: boolean;
      isPrintReady?: boolean;
      hasTestPrintSelected?: boolean;
    };
    email?: string | null;
    rocketLauncherEmail?: string;
    features?: LaunchPadFeatures;
    inventory?: {
      provider?: InventoryType;
      otherProviderName?: string;
    };
    crm?: {
      primaryLoginId?: string;
      enabledVinSolutionsPartnerIntegration?: boolean;
    };
    otherCrmName?: string; // deprecated REL-6569
    creditPricing?: {
      [FinanceType.PURCHASE]?: {
        creditTiers?: CreditTier[];
        pricing?: CreditPricingPurchasePricing[][];
        terms?: CreditPricingPurchaseTerm[];
      };
      [FinanceType.LEASE]?: {
        creditTiers?: CreditTier[];
        pricing?: CreditPricingLeasePricing[][];
        terms?: CreditPricingLeaseTerm[];
      };
    };
    status?: {
      finishedPrintingSetup?: boolean;
      finishedProductSelect?: boolean;
      finishedUsersConfig?: boolean;
      finishedFormsConfig?: boolean;
      finishedWidgetConfig?: boolean;
      finishedPricingLeaseCopyOver?: boolean;
      finishedPricingValidation?: boolean;
      finishedTradeInManagerSelection?: boolean;
      implementerVerifiedInventory?: boolean;
      readyToBeginInventoryConfig?: boolean;
      readyToLaunch?: boolean;
    };
    firstLogin?: string;
    lastLogin?: string;
  } | null;

  createdAt?: string | null;
  liveAt?: string | null;
  supportEmail?: string | null;
  otherCrmName?: string;
  otherDmsName?: string;
}

export enum PricingIncorrectReason {
  VEHICLE_PRICE_WRONG = 'vehicle_pricing_wrong',
  FEES_INCORRECT = 'fees_incorrect',
  TAXES_INCORRECT = 'taxes_incorrect',
  OTHER = 'other',
}

export interface FinanceTypePricingValidation {
  [FinanceType.CASH]: PricingIncorrectState;
  [FinanceType.PURCHASE]: PricingIncorrectState;
  [FinanceType.LEASE]: PricingIncorrectState;
}

export interface PricingIncorrectState {
  correct: boolean;
  reasons?: {
    [index in PricingIncorrectReason]?: {
      value: boolean;
      message: string;
    };
  };
}

export enum PricingValidationType {
  NEW = 'New',
  Used = 'Used',
}

export enum PricingValidationClientType {
  INSTORE = 'Instore',
  ONLINE = 'Online',
}
